/* eslint-disable camelcase */
import { BatchDocument } from "../store/ducks/batchDocuments.ducks"
import {
  ApiImportStatus,
  ArchiveDetail,
  ArchiveDetailWithApiStatus,
  isArchiveDetailWithAcdApiStatus,
  isArchiveDetailWithApiStatus,
  isArchiveDetailWithFulllApiStatus,
  isArchiveDetailWithInqomApiStatus,
  isArchiveDetailWithPennylaneApiStatus,
} from "../store/ducks/writings.ducks"
import { dateMatchesUserInput } from "./date"
import { capitalizeFirstLetter, normalizeString } from "./string"

export function filterBatches(
  batches: BatchDocument[],
  search: string,
  customDateActive?: boolean,
  customDateRange?: [Date, Date] | null
): BatchDocument[] {
  const dateDefined = customDateActive && customDateRange

  if (!batches || batches.length === 0) return []
  if (!search && !dateDefined) return batches

  return [
    ...batches.filter(
      (batch) =>
        (batch?.userBatchName?.toLowerCase().includes(search.toLowerCase()) ||
          batch?.originalFileName
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          batch.email?.toLowerCase()?.includes(search.toLowerCase()) ||
          getFileExtension(batch?.originalFileName)
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          dateMatchesUserInput(
            search.toLowerCase(),
            batch.createdAt.toString()
          )) &&
        (dateDefined
          ? new Date(batch.createdAt) >= customDateRange[0] &&
            new Date(batch.createdAt) <= customDateRange[1]
          : true)
    ),
  ]
}

export function filterArchivedBatches(
  batches: ArchiveDetail[],
  search: string
) {
  if (!search) {
    return batches
  }

  return batches.filter((batch) => {
    return (
      normalizeString(batch.original_file_name).includes(
        normalizeString(search)
      ) ||
      normalizeString(batch?.email || "").includes(normalizeString(search)) ||
      normalizeString(batch.full_doc_name).includes(normalizeString(search))
    )
  })
}

export function sortBatchesAlphabetically(
  batches: BatchDocument[],
  asc: boolean
) {
  return [
    ...batches.sort((a, b) =>
      asc
        ? a?.originalFileName.localeCompare(b?.originalFileName)
        : b?.originalFileName.localeCompare(a?.originalFileName)
    ),
  ]
}

export function sortBatchesByType(batches: BatchDocument[], asc: boolean) {
  return [
    ...batches.sort((a, b) =>
      asc
        ? getFileExtension(a?.originalFileName).localeCompare(
            getFileExtension(b?.originalFileName)
          )
        : getFileExtension(b?.originalFileName).localeCompare(
            getFileExtension(a?.originalFileName)
          )
    ),
  ]
}

export function sortBatchesDepositBy(batches: BatchDocument[], asc: boolean) {
  return [
    ...batches.sort((a, b) => {
      return asc
        ? (a?.email || "zzzzzzzzzzzzzz").localeCompare(
            b?.email || "zzzzzzzzzzzzzz"
          )
        : (b?.email || "a").localeCompare(a?.email || "a")
    }),
  ]
}

export function sortBatchesByDate(batches: BatchDocument[], asc: boolean) {
  return [...batches.sort((a, b) => (asc ? a.id - b.id : b.id - a.id))]
}

export function sortDisplayedNameBatches(
  batches: ArchiveDetail[],
  asc: boolean
) {
  return [
    ...batches.sort((a, b) =>
      asc
        ? a.original_file_name.localeCompare(b.original_file_name)
        : b.original_file_name.localeCompare(a.original_file_name)
    ),
  ]
}

export function sortDocumentNameBatches(
  batches: ArchiveDetail[],
  asc: boolean
) {
  return [
    ...batches.sort((a, b) =>
      asc
        ? a.full_doc_name.localeCompare(b.full_doc_name)
        : b.full_doc_name.localeCompare(a.full_doc_name)
    ),
  ]
}

export function sortBatchDocumentByEmail(
  batches: ArchiveDetail[],
  asc: boolean
) {
  let allBatches: ArchiveDetail[] = []
  const validEmails = batches.filter((b) => b.email !== "null")
  const invalidEmails = batches.filter((b) => b.email === "null")

  const sortedEmails = validEmails.sort((a, b) => {
    return a.email.localeCompare(b.email)
  })

  asc
    ? allBatches.push(...sortedEmails, ...invalidEmails)
    : allBatches.push(...invalidEmails, ...sortedEmails)

  return [...(asc ? allBatches : allBatches.reverse())]
}

export const sortDisplayedBatchByDate = (
  batches: ArchiveDetail[],
  asc: boolean
) => {
  return [
    ...batches.sort((a, b) =>
      asc ? a.batch_id - b.batch_id : b.batch_id - a.batch_id
    ),
  ]
}

const archiveReentrySortKey = (
  row: ArchiveDetail | ArchiveDetailWithApiStatus
) =>
  (row as ArchiveDetail).full_doc_id &&
  (row as ArchiveDetail).full_document_status === "assigned"
    ? 1
    : 0

export const sortDisplayedBatchByArchiveReentry = <
  T extends ArchiveDetail | ArchiveDetailWithApiStatus
>(
  batches: T[],
  asc: boolean
) => {
  return [
    ...batches.sort((a, b) => {
      const diff = archiveReentrySortKey(b) - archiveReentrySortKey(a)
      return asc ? -diff : diff
    }),
  ]
}

const sortBatchesByApiStatus = <
  T extends ArchiveDetail | ArchiveDetailWithApiStatus
>(
  batches: T[],
  asc: boolean,
  typeGuard: (item: T) => boolean,
  getStatus: (item: T) => ApiImportStatus
) => {
  const statusesToInt: Record<ApiImportStatus, number> = {
    imported: 3,
    in_progress: 2,
    import_error: 1,
  }

  return [
    ...batches.sort((a, b) => {
      if (!typeGuard(a) && typeGuard(b)) {
        return asc ? 1 : -1
      } else if (typeGuard(a) && !typeGuard(b)) {
        return asc ? -1 : 1
      } else if (!typeGuard(a) && !typeGuard(b)) {
        return asc ? 1 : -1
      }
      if (!typeGuard(a) || !typeGuard(b)) {
        // this branch is here for typing, should not execute
        return asc ? 1 : -1
      }

      return asc
        ? statusesToInt[getStatus(a)] - statusesToInt[getStatus(b)]
        : statusesToInt[getStatus(b)] - statusesToInt[getStatus(a)]
    }),
  ]
}

export const sortDisplayedBatchByStatus = (
  batches: (ArchiveDetail | ArchiveDetailWithApiStatus)[],
  asc: boolean
) => {
  if (!batches.some((b) => isArchiveDetailWithApiStatus(b))) {
    return batches
  }

  if (batches.some((b) => isArchiveDetailWithPennylaneApiStatus(b))) {
    return sortBatchesByApiStatus(
      batches,
      asc,
      isArchiveDetailWithPennylaneApiStatus,
      (item) =>
        isArchiveDetailWithPennylaneApiStatus(item)
          ? item.pennylane_import_status
          : "import_error"
    )
  }

  if (batches.some((b) => isArchiveDetailWithAcdApiStatus(b))) {
    return sortBatchesByApiStatus(
      batches,
      asc,
      isArchiveDetailWithAcdApiStatus,
      (item) =>
        isArchiveDetailWithAcdApiStatus(item)
          ? item.acd_import_status
          : "import_error"
    )
  }

  if (batches.some((b) => isArchiveDetailWithFulllApiStatus(b))) {
    return sortBatchesByApiStatus(
      batches,
      asc,
      isArchiveDetailWithFulllApiStatus,
      (item) =>
        (isArchiveDetailWithFulllApiStatus(item) && item.fulll_import_status) ||
        "import_error"
    )
  }

  if (batches.some((b) => isArchiveDetailWithInqomApiStatus(b))) {
    return sortBatchesByApiStatus(
      batches,
      asc,
      isArchiveDetailWithInqomApiStatus,
      (item) =>
        isArchiveDetailWithInqomApiStatus(item)
          ? item.inqom_import_status
          : "import_error"
    )
  }

  return batches
}

export function getDocumentTypeDisplayed(batch: BatchDocument) {
  const extension = getFileExtension(batch.originalFileName)
  return capitalizeFirstLetter(extension?.replace(".", ""))
}

export function getListWithoutDuplicates(batches: BatchDocument[]) {
  return [...batches.filter((batch) => batch.duplicate === false)]
}

export function getListOfOfficeDocuments(batches: BatchDocument[]) {
  return [...batches.filter((batch) => batch.exchange === true)]
}

export function getFileExtension(fileName: string) {
  return fileName?.substring(fileName.lastIndexOf(".")).toLowerCase()
}

const NOT_EXCHANGE_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".pdf",
  ".tiff",
  ".tif",
]
export function isExchange(fileName: string): boolean {
  return !NOT_EXCHANGE_EXTENSIONS.includes(getFileExtension(fileName))
}

export function sortBatchesByState(batches: BatchDocument[], asc: boolean) {
  return [
    ...batches.sort((a, b) =>
      asc
        ? a.duplicate === b.duplicate
          ? 0
          : a.duplicate
          ? 1
          : -1
        : a.duplicate === b.duplicate
        ? 0
        : a.duplicate
        ? -1
        : 1
    ),
  ]
}
